import React from "react";
import {
  CircularProgress,
  InputAdornment,
  MenuItem,
  TextField,
  TextFieldProps,
  Typography,
} from "@mui/material";

import { ArrowDownIconPlus as ArrowIcon } from "@components/Icons";
import { SelectOption } from "./interface";
import { MuiOutlinedInputStyles, MuiSelectIconStyles } from "./styles";

export interface SelectFieldProps
  extends Omit<TextFieldProps, "select" | "onChange"> {
  options: SelectOption[];
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  loading?: boolean;
  formatLabel?: (value: unknown, options: SelectOption[]) => string;
  renderMenuItem?: (option: SelectOption) => React.ReactNode;
}

const defaultFormatLabel = (value: unknown, options: SelectOption[]) =>
  options.find((o) => o.value === value)?.label ?? "";

export const SelectField: React.FC<SelectFieldProps> = ({
  options,
  value,
  onChange,
  loading = false,
  formatLabel = defaultFormatLabel,
  renderMenuItem,
  ...props
}) => {
  const getLabel = (val: unknown) => formatLabel(val, options);

  return (
    <TextField
      {...props}
      select
      variant="outlined"
      value={value ?? ""}
      onChange={onChange}
      disabled={loading || props.disabled}
      SelectProps={{
        IconComponent: loading ? () => null : ArrowIcon,
        renderValue: (selectedValue) => (
          <Typography variant="body1" noWrap title={getLabel(selectedValue)}>
            {getLabel(selectedValue)}
          </Typography>
        ),
        endAdornment: loading ? (
          <InputAdornment position="end">
            <CircularProgress size={20} sx={{ mr: 2 }} />
          </InputAdornment>
        ) : undefined,
      }}
      sx={{
        ...MuiSelectIconStyles,
        ...MuiOutlinedInputStyles,
        ...props.sx,
      }}
    >
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {renderMenuItem ? renderMenuItem(option) : option.label}
        </MenuItem>
      ))}
    </TextField>
  );
};
